import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { AddressControl, AddressControlDTO } from "@/Common/Components/Contacts/AddressControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";

import styles from "./AddressesEdit.module.scss";

interface IAddressesEditProps {
    addresses: Address[];
    onAdd: () => void;
    onRemove: (address: Address) => void;
    onNotifyParentCallback: (address: Address, dto: AddressControlDTO, index: number) => void;
    onContactTypeChange: (value: string, index: number) => void;
    onPreferenceChange: (isChecked: boolean, index: number) => void;
}

function AddressesEdit(props: IAddressesEditProps): JSX.Element {
    const { getText } = useTranslation();

    return (
        <>
            <div>
                <span>{getText("AddressLabel")}</span>
            </div>
            {props.addresses
                .sortByPropertyDesc(p => p.IsPreferred)
                .map((address, idx) => (
                    <div key={`address_${address.ContactPoint_id}`} className="flex-container">
                        <table>
                            <tbody>
                                <tr>
                                    <td className="c1">
                                        <span className="float-right">{getText("AddressTypeLabel")}</span>
                                    </td>
                                    <td className="c2">
                                        <SelectLookupControl
                                            dataCy="patientProfile-contacts-address-type"
                                            IncludeEmptyChoice={false}
                                            Value={address.ContactPointTypeLookupId}
                                            AvailableValues={cacheStore.GetContactPointTypeLookups()
                                                .map(lookUp => lookUp.ToComboboxItem())
                                            }
                                            Item={address}
                                            Callback={(value: string) => props.onContactTypeChange(value, idx)}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                            <AddressControl
                                id={`ContactEdit_Address_${address.ContactPoint_id}`}
                                dataCy="patientProfile-contacts-address"
                                initialStreetAddress={address.StreetAddress}
                                initialCity={address.City}
                                initialPostalCode={address.ZipCode}
                                initialCountryLookupId={address.CountryLookupId}
                                initialStateLookupId={address.StateLookupId}
                                renderAsTableBody
                                alwaysShowCityAndZipCode
                                onNotifyParentCallback={dto => props.onNotifyParentCallback(address, dto, idx)}
                            />
                            <tbody>
                                <tr>
                                    <td className="c1">
                                        <span className="float-right" />
                                    </td>
                                    <td className="c2">
                                        <CheckboxControl
                                            dataCy="patientProfile-contacts-address-isPreferred"
                                            checked={address.IsPreferred}
                                            label={getText("PreferredAddressDLabel")}
                                            item={address}
                                            onChange={checked => props.onPreferenceChange(checked, idx)}
                                        />
                                    </td>
                                </tr>
                                {(idx === props.addresses.length - 1 || props.addresses.length === 0) && (
                                    <tr key="addAddress">
                                        <td className="c1">
                                            <span className="float-right" />
                                        </td>
                                        <td className="c2 text-right">
                                            <a onClick={() => props.onAdd()}>
                                                {getText("AddLabel")}
                                            </a>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <div className={`${styles.removeButton} c3 with-line margin-md-right`}>
                            <span
                                className={styles.removeIcon}
                                onClick={() => props.onRemove(address)}
                            >X
                            </span>
                        </div>
                    </div>
                ))}
        </>
    );
}

export { AddressesEdit };
